import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const features = [
  {
    icon: 'sparkles',
    title: 'Easy when you want it',
    desc: 'Shape a story with moods, quick cleanup, and guided tools that speak plain language.',
  },
  {
    icon: 'sliders',
    title: 'Precise when you need it',
    desc: 'Switch to Studio for detailed trim, color, audio, speed, and caption controls.',
  },
  {
    icon: 'timeline',
    title: 'A timeline that stays clear',
    desc: 'Arrange moments visually, understand duration at a glance, and keep your flow moving.',
  },
  {
    icon: 'shield',
    title: 'Originals stay untouched',
    desc: 'Every edit is project metadata. Your source media remains exactly as you imported it.',
  },
  {
    icon: 'captions',
    title: 'Finish the story',
    desc: 'Add captions, balance sound, tune the look, and preview the result before export.',
  },
  {
    icon: 'export',
    title: 'Ready when you are',
    desc: 'Create a new output, save it to your gallery, or share it without touching the source.',
  },
];

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function FeatureIcon({ name }) {
  const paths = {
    sparkles: <path d="m12 3 .8 2.7A4.8 4.8 0 0 0 16 9l2.7.8-2.7.8a4.8 4.8 0 0 0-3.2 3.2L12 16.5l-.8-2.7A4.8 4.8 0 0 0 8 10.6l-2.7-.8L8 9a4.8 4.8 0 0 0 3.2-3.3L12 3Zm6 11 .4 1.3a2.4 2.4 0 0 0 1.6 1.6l1.3.4-1.3.4a2.4 2.4 0 0 0-1.6 1.6L18 20.6l-.4-1.3a2.4 2.4 0 0 0-1.6-1.6l-1.3-.4 1.3-.4a2.4 2.4 0 0 0 1.6-1.6L18 14Z" />,
    sliders: <path d="M4 7h5m4 0h7M9 4v6m-5 7h9m4 0h3m-3-3v6" />,
    timeline: <path d="M4 7h16M4 12h16M4 17h16M8 5v4m7 1v4m3 1v4" />,
    shield: <path d="M12 3 5 6v5c0 4.4 2.8 8.3 7 10 4.2-1.7 7-5.6 7-10V6l-7-3Zm-3 9 2 2 4-4" />,
    captions: <path d="M4 6h16v12H4V6Zm3 4h4m2 0h4M7 14h3m2 0h5" />,
    export: <path d="M12 15V3m0 0L8 7m4-4 4 4M5 11v8h14v-8" />,
  };
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

function EditorMockup() {
  return (
    <div className="editor-mockup" aria-label="KinetoCut editor interface preview">
      <div className="mockup-titlebar">
        <div className="window-dots"><span /><span /><span /></div>
        <div className="mockup-project"><span className="live-dot" /> Weekend story <small>Saved</small></div>
        <div className="mockup-export">Export</div>
      </div>
      <div className="mockup-workspace">
        <aside className="mockup-tools" aria-hidden="true">
          <span className="selected">✦</span><span>⌁</span><span>◐</span><span>♪</span><span>CC</span>
        </aside>
        <div className="mockup-stage">
          <div className="mockup-video">
            <div className="video-sky" />
            <div className="video-hill hill-back" />
            <div className="video-hill hill-front" />
            <div className="video-sun" />
            <button type="button" className="mock-play" aria-label="Preview play button">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="m9 7 8 5-8 5V7Z" /></svg>
            </button>
            <div className="mock-caption">Weekend escape</div>
            <div className="video-progress"><span /></div>
          </div>
          <div className="mockup-timeline">
            <div className="timeline-head"><span>Timeline</span><small>4 clips · 00:28</small></div>
            <div className="timeline-ruler"><i /><i /><i /><i /><i /></div>
            <div className="timeline-clips">
              <span className="clip-one">01 · 00:06</span>
              <span className="clip-two">02 · 00:08</span>
              <span className="clip-three">03 · 00:05</span>
              <span className="clip-four">04 · 00:09</span>
              <b />
            </div>
            <div className="audio-track"><span /><span /><span /><span /><span /><span /><span /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { user, isAuthenticated } = useAuth();
  const firstName = (user?.name || user?.email || 'Editor').split(/[ @]/)[0];

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="ambient-orb orb-one" />
        <div className="ambient-orb orb-two" />
        <div className="page-container hero-grid">
          <div className="hero-copy">
            <div className="eyebrow">
              <span className="eyebrow-icon">✦</span>
              {isAuthenticated ? `${greeting()}, ${firstName}` : 'Mobile editing, thoughtfully designed'}
            </div>
            <h1>Your story.<br /><span>Cut with precision.</span></h1>
            <p className="hero-lede">
              A focused video editor that feels approachable on day one and powerful when you want finer control.
            </p>
            <div className="hero-actions">
              <Link to={isAuthenticated ? '/profile' : '/register'} className="button button-primary button-large">
                {isAuthenticated ? 'View your account' : 'Start creating'}
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
              </Link>
              <Link to="/#features" className="button button-secondary button-large">Explore features</Link>
            </div>
            <div className="hero-trust">
              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="m5 12 4 4L19 6" /></svg>No learning curve required</span>
              <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 3 5 6v5c0 4.4 2.8 8.3 7 10 4.2-1.7 7-5.6 7-10V6l-7-3Z" /></svg>Original media stays safe</span>
            </div>
          </div>
          <div className="hero-visual">
            <div className="visual-badge badge-easy"><span>✦</span><strong>Easy mode</strong><small>Guided edits</small></div>
            <EditorMockup />
            <div className="visual-badge badge-studio"><span>⌁</span><strong>Studio</strong><small>Precise control</small></div>
          </div>
        </div>
      </section>

      <section className="proof-strip" aria-label="Product highlights">
        <div className="page-container proof-grid">
          <div><strong>2</strong><span>editing experiences</span></div>
          <div><strong>100%</strong><span>non-destructive workflow</span></div>
          <div><strong>One flow</strong><span>from import to share</span></div>
        </div>
      </section>

      <section className="section-shell" id="features">
        <div className="page-container">
          <div className="section-heading">
            <div className="eyebrow">Built around your footage</div>
            <h2>Everything you need.<br /><span>Nothing in the way.</span></h2>
            <p>Move from a rough collection of moments to a polished story without losing your place—or your originals.</p>
          </div>
          <div className="feature-grid">
            {features.map((feature, index) => (
              <article className="feature-card" key={feature.title}>
                <span className="feature-number">0{index + 1}</span>
                <div className="feature-icon"><FeatureIcon name={feature.icon} /></div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell workflow-section">
        <div className="page-container workflow-grid">
          <div className="workflow-copy">
            <div className="eyebrow">A calmer creative process</div>
            <h2>From camera roll<br />to finished story.</h2>
            <p>KinetoCut keeps the next step obvious, whether you want a quick draft or a detailed edit.</p>
            <Link to={isAuthenticated ? '/profile' : '/register'} className="text-link">
              {isAuthenticated ? 'Go to your account' : 'Create your account'} <span>→</span>
            </Link>
          </div>
          <ol className="workflow-list">
            <li><span>01</span><div><strong>Bring in your moments</strong><p>Select the clips that belong in your story. Safe working copies protect the originals.</p></div></li>
            <li><span>02</span><div><strong>Shape the flow</strong><p>Choose Easy for guidance or Studio for frame-by-frame control, then preview every change.</p></div></li>
            <li><span>03</span><div><strong>Export with confidence</strong><p>Create a brand-new video to save or share when the story feels right.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="cta-section">
        <div className="page-container">
          <div className="cta-card">
            <div className="cta-glow" />
            <div>
              <div className="eyebrow">Your next story starts here</div>
              <h2>Make the cut feel effortless.</h2>
              <p>Sign in to KinetoCut and keep your creative space ready.</p>
            </div>
            <Link to={isAuthenticated ? '/profile' : '/register'} className="button button-primary button-large">
              {isAuthenticated ? 'Open account' : 'Get started free'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
